import { demoMovers, demoOverview, demoRankings, demoSync, demoTraffic } from "./demo";
import type { RankHistoryGridResponse, RankHistoryResponse, RankingRow } from "../types";

const PRODUCTION_API_URL = "https://agrowth-seo-api.agrowth-seo-bathanh.workers.dev";
const API_URL = (
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD ? PRODUCTION_API_URL : "")
).replace(/\/$/, "");
export const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true" || !API_URL;

export interface QueryFilters {
  from?: string;
  to?: string;
  compareFrom?: string;
  compareTo?: string;
  country?: string;
  projectId?: string;
  category?: string;
  query?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  includeDates?: boolean;
}

function queryString(filters: object): string {
  const params = new URLSearchParams();
  Object.entries(filters as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const value = params.toString();
  return value ? `?${value}` : "";
}

async function request<T>(path: string, fallback: T, init?: RequestInit): Promise<T> {
  if (isDemoMode) {
    await new Promise((resolve) => setTimeout(resolve, 180));
    return fallback;
  }
  const response = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    if (response.status === 401 && !path.startsWith("/api/v1/auth/")) {
      window.dispatchEvent(new Event("agrowth-auth-expired"));
    }
    throw new ApiError(response.status, payload?.error ?? response.statusText);
  }
  const json = (await response.json()) as { data: T };
  return json.data;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  generatedAt?: string;
  statusCounts?: Record<string, number>;
  range?: Record<string, string>;
}

export interface Paginated<T> {
  data: T;
  meta: PageMeta;
}

export interface ProjectOption {
  source_project_id: number;
  name: string;
  country: string;
  category: string;
}

export interface FilterOptions {
  projects: ProjectOption[];
  countries: string[];
  categories: string[];
}

export type SyncProfile = "quick" | "aggregate-backfill" | "detailed-backfill" | "custom";
export type SyncModule =
  | "serprobot"
  | "gsc-site"
  | "gsc-country"
  | "gsc-page"
  | "gsc-page-query"
  | "ga4";

export interface StartSyncRequest {
  profile: SyncProfile;
  modules?: SyncModule[];
  from?: string;
  to?: string;
  overwrite?: boolean;
  backfillDays?: number;
}

export interface SyncRun {
  id: string;
  source: string;
  trigger_type: string;
  profile?: SyncProfile;
  status: string;
  control_state?: string;
  tasks_total: number;
  tasks_completed: number;
  units_total?: number;
  units_completed?: number;
  rows_upserted: number;
  rows_inserted?: number;
  rows_updated?: number;
  rows_deleted?: number;
  rows_skipped?: number;
  errors_count: number;
  started_at: string;
  completed_at?: string | null;
  updated_at?: string;
}

export interface SyncStatusResponse {
  runs: SyncRun[];
  latestTasks: Array<Record<string, unknown>>;
  failedTasks: Array<Record<string, unknown>>;
  sourceData: {
    serprobot_latest_date: string | null;
    gsc_latest_date: string | null;
    gsc_page_latest_date: string | null;
    ga4_latest_date: string | null;
    ga4Configured: boolean;
  };
}

export interface RankDistribution {
  source_project_id: number;
  name: string;
  country: string;
  category: string;
  average_rank: number | null;
  top3: number;
  top10: number;
  top30: number;
  top100: number;
  not_found: number;
  keyword_count: number;
}

export interface UrlRow {
  id: number;
  canonical_url: string;
  path: string;
  page_group: string;
  topic: string | null;
  clicks: number;
  impressions: number;
  ctr: number;
  gsc_position: number | null;
  sessions: number;
  engagement_rate: number;
  avg_engagement_seconds: number | null;
  key_events: number;
  average_rank: number | null;
  top10_keywords: number;
  score: number;
  status: string;
  priority: number;
  reasons: string[];
  changes: {
    clicks: number;
    impressions: number;
    sessions: number;
    ctr: number;
    gscPosition: number;
    engagementRate: number;
    avgEngagementSeconds: number | null;
    keyEvents: number;
    averageRank: number;
  };
}

export interface QueryInsightRow {
  queryOriginal: string;
  queryNormalized: string;
  branded: boolean;
  isNew: boolean;
  current: { clicks: number; impressions: number; ctr: number; position: number | null };
  previous: { clicks: number; impressions: number; ctr: number; position: number | null };
  delta: {
    clicks: number;
    impressions: number;
    ctr: number;
    positionLoss: number | null;
    impressionGrowthPct: number | null;
    clickGrowthPct: number | null;
  };
}

export interface QueryInsightMeta extends PageMeta {
  type: "trending" | "losing";
  windowDays: 7 | 28;
  country: string | null;
  brand: "all" | "brand" | "nonbrand";
  current: { from: string | null; to: string | null; availableDays: number };
  previous: { from: string | null; to: string | null; availableDays: number };
  latestDataAt: string | null;
  complete: boolean;
  thresholds: { minImpressions: number; minPositionLoss: number };
  brandTerms?: string[];
}

const demoHistoryDates = [
  "2026-07-18",
  "2026-07-20",
  "2026-07-22",
  "2026-07-24",
  "2026-07-26"
];

const demoHistoryGrid: RankHistoryGridResponse = {
  dates: demoHistoryDates,
  rows: demoRankings.slice(0, 12).map((row, index) => ({
    ...row,
    keyword_id: index + 1,
    source_keyword_id: row.source_keyword_id ?? index + 1,
    search_volume_local: (index + 1) * 10
  })),
  history: demoRankings.slice(0, 12).flatMap((row, index) =>
    demoHistoryDates.map((date, dateIndex) => ({
      keyword_id: index + 1,
      rank_date: date,
      position:
        row.rank_position == null
          ? null
          : Math.max(1, Number(row.rank_position) + ((index + dateIndex) % 5) - 2),
      is_found: row.rank_position == null ? 0 : 1,
      found_url: row.found_url ?? null
    }))
  )
};

async function paginatedRequest<T>(path: string, fallback: T): Promise<Paginated<T>> {
  if (isDemoMode) {
    await new Promise((resolve) => setTimeout(resolve, 180));
    const rows = Array.isArray(fallback)
      ? fallback
      : (fallback as { rows?: unknown[] }).rows ?? [];
    return {
      data: fallback,
      meta: { page: 1, pageSize: 50, total: rows.length }
    };
  }
  const response = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" }
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    if (response.status === 401) window.dispatchEvent(new Event("agrowth-auth-expired"));
    throw new ApiError(response.status, payload?.error ?? response.statusText);
  }
  return (await response.json()) as Paginated<T>;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export const seoApi = {
  dataAvailability: () =>
    request<Record<"site" | "query" | "page" | "ga4", string[]>>(
      "/api/v1/data-availability",
      {
        site: demoOverview.daily.map((row) => row.date),
        query: demoOverview.daily.map((row) => row.date),
        page: demoOverview.daily.map((row) => row.date),
        ga4: []
      }
    ),
  filterOptions: () =>
    request<FilterOptions>(
      "/api/v1/filter-options",
      {
        projects: [],
        countries: ["USA", "GBR", "IND", "RUS", "UKR"],
        categories: ["blog", "services"]
      }
    ),
  rankDistributions: (to: string) =>
    request<RankDistribution[]>(
      `/api/v1/rank-distributions${queryString({ to })}`,
      [
        {
          source_project_id: 5109280,
          name: "Keyword US Service",
          country: "USA",
          category: "services",
          average_rank: 0,
          top3: 0,
          top10: 0,
          top30: 0,
          top100: 0,
          not_found: 0,
          keyword_count: 0
        },
        {
          source_project_id: 5109283,
          name: "Keyword UK Services",
          country: "GBR",
          category: "services",
          average_rank: 0,
          top3: 0,
          top10: 0,
          top30: 0,
          top100: 0,
          not_found: 0,
          keyword_count: 0
        },
        {
          source_project_id: 5109285,
          name: "Keyword India Services",
          country: "IND",
          category: "services",
          average_rank: 0,
          top3: 0,
          top10: 0,
          top30: 0,
          top100: 0,
          not_found: 0,
          keyword_count: 0
        }
      ]
    ),
  session: () => request("/api/v1/auth/session", { authenticated: true }),
  login: (password: string) =>
    request(
      "/api/v1/auth/login",
      { authenticated: true },
      { method: "POST", body: JSON.stringify({ password }) }
    ),
  logout: () =>
    request(
      "/api/v1/auth/logout",
      { authenticated: false },
      { method: "POST" }
    ),
  overview: (filters: QueryFilters) =>
    request(`/api/v1/overview${queryString(filters)}`, demoOverview),
  rankings: (filters: QueryFilters) =>
    paginatedRequest<RankingRow[]>(`/api/v1/rankings${queryString(filters)}`, demoRankings),
  rankHistoryGrid: (filters: QueryFilters) =>
    paginatedRequest<RankHistoryGridResponse>(
      `/api/v1/ranking-history-grid${queryString(filters)}`,
      demoHistoryGrid
    ),
  rankHistory: (keywordId: number, from: string, to: string) =>
    request<RankHistoryResponse>(
      `/api/v1/rankings/${keywordId}/history${queryString({ from, to })}`,
      {
        keyword: demoRankings.find((row) => row.source_keyword_id === keywordId) ?? demoRankings[0],
        history: []
      } as RankHistoryResponse
    ),
  traffic: (filters: QueryFilters) =>
    paginatedRequest(`/api/v1/traffic${queryString(filters)}`, demoTraffic),
  matrix: (filters: QueryFilters) =>
    paginatedRequest(`/api/v1/ranking-traffic${queryString(filters)}`, demoRankings),
  movers: (filters: QueryFilters) =>
    request(`/api/v1/movers${queryString(filters)}`, demoMovers),
  queryInsights: async (filters: {
    type: "trending" | "losing";
    window: 7 | 28;
    country?: string;
    brand?: "all" | "brand" | "nonbrand";
    minImpressions: number;
    minPositionLoss: number;
    query?: string;
    page: number;
    pageSize: number;
  }) => {
    if (isDemoMode) {
      const latest = new Date().toISOString().slice(0, 10);
      return {
        data: { rows: [] },
        meta: {
          type: filters.type,
          windowDays: filters.window,
          country: filters.country || null,
          brand: filters.brand ?? "all",
          current: { from: latest, to: latest, availableDays: filters.window },
          previous: { from: latest, to: latest, availableDays: filters.window },
          latestDataAt: latest,
          complete: true,
          thresholds: {
            minImpressions: filters.minImpressions,
            minPositionLoss: filters.minPositionLoss
          },
          page: filters.page,
          pageSize: filters.pageSize,
          total: 0
        } satisfies QueryInsightMeta
      };
    }
    return paginatedRequest<{ rows: QueryInsightRow[] }>(
      `/api/v1/query-insights${queryString(filters)}`,
      { rows: [] }
    ) as Promise<Paginated<{ rows: QueryInsightRow[] }> & { meta: QueryInsightMeta }>;
  },
  syncStatus: (runId?: string) =>
    request<SyncStatusResponse>(
      `/api/v1/sync-status${runId ? `?runId=${encodeURIComponent(runId)}` : ""}`,
      demoSync as SyncStatusResponse
    ),
  syncEvents: (runId: string, after = "") =>
    request<{ events: Array<Record<string, unknown>>; cursor: string }>(
      `/api/v1/sync-runs/${encodeURIComponent(runId)}/events${after ? `?after=${encodeURIComponent(after)}` : ""}`,
      { events: [], cursor: after }
    ),
  startSync: (payload: StartSyncRequest) =>
    request(
      "/api/v1/sync",
      { runId: `demo-${Date.now()}`, status: "running" },
      { method: "POST", body: JSON.stringify(payload) }
    ),
  controlSync: (runId: string, action: "pause" | "resume" | "cancel") =>
    request(
      `/api/v1/sync-runs/${encodeURIComponent(runId)}/${action}`,
      { ok: true },
      { method: "POST" }
    ),
  retrySyncTask: (taskId: string) =>
    request(
      `/api/v1/sync-tasks/${encodeURIComponent(taskId)}/retry`,
      { ok: true },
      { method: "POST" }
    ),
  retryFailedSyncTasks: (runId: string) =>
    request(
      `/api/v1/sync-runs/${encodeURIComponent(runId)}/retry-failed`,
      { queued: 0 },
      { method: "POST" }
    ),
  urlOverview: (filters: QueryFilters) =>
    request<any>(`/api/v2/overview${queryString(filters)}`, {
      metrics: { clicks: 0, impressions: 0, ctr: 0, sessions: 0, engagementRate: 0, top10Keywords: 0 },
      previous: {}, daily: [],
      coverage: {
        gsc: { currentDays: 0, previousDays: 0, expectedCurrentDays: 28, expectedPreviousDays: 28, comparisonReady: false },
        ga4: { currentDays: 0, previousDays: 0, expectedCurrentDays: 28, expectedPreviousDays: 28, comparisonReady: false }
      },
      sources: { ga4Configured: false }
    }),
  urls: (filters: QueryFilters & { group?: string; status?: string }) =>
    paginatedRequest<UrlRow[]>(`/api/v2/urls${queryString(filters)}`, []),
  urlDetail: (id: number, filters: QueryFilters) =>
    request<any>(`/api/v2/urls/${id}${queryString(filters)}`, {
      page: { id, path: "/demo-url", page_group: "blog", topic: "demo" },
      performance: { score: 68, status: "potential", reasons: ["ranking_opportunity"] },
      previous: {}, queries: [], countries: [], daily: [],
      coverage: {
        gsc: { currentDays: 0, previousDays: 0, expectedCurrentDays: 28, expectedPreviousDays: 28, comparisonReady: false },
        ga4: { currentDays: 0, previousDays: 0, expectedCurrentDays: 28, expectedPreviousDays: 28, comparisonReady: false }
      },
      sources: { ga4Configured: false }
    })
};
